import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {DatastoreDto, EngineTagService, Partner, TagDTO} from '@tj/tj-dmp-lib-api';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";

@Component({
    selector: 'dmp-tags',
    templateUrl: './tags.component.html',
    styleUrls: ['./tags.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class TagsComponent implements OnInit {

    breadcrumb = [
        {label: this.translate.instant('menu.data-center.label')},
        {label: this.translate.instant('menu.data-center.tags')}
    ];
    description = this.translate.instant('tag.list.description');

    editPanel = false;
    isNew = false;

    form: UntypedFormGroup;
    submitted = false;

    group: DatastoreDto;
    partner: Partner;

    actual: TagDTO;
    data: TagDTO[];
    selectedTags: TagDTO[] = [];
    datastore: DatastoreDto;
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];
    private actionItemsByTagId: Record<number, MenuItem[]> = {};

    @ViewChild('tagFileInput') tagFileInput: ElementRef;

    constructor(private router: Router,
                private confirmationService: ConfirmationService,
                private translate: TranslateService,
                private messageService: MessageService,
                private engineTagService: EngineTagService,
                private fb: UntypedFormBuilder) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.data-center.label')},
            {label: this.translate.instant('menu.data-center.tags')}
        ];
        this.description = this.translate.instant('tag.list.description');
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            id: [''],
            tenant: ['', Validators.required],
            partners: [[]],
            name: ['', Validators.required],
            description: [''],
            enable: ['']
        });
        this.initExportMenu();
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        this.updateListTable();
    }

    private updateListTable() {
        if (this.datastore) {
            this.engineTagService.getAllTags(this.datastore.code, null)
                .subscribe((res) => {
                    this.data = res;
                    this.actionItemsByTagId = {};
                    this.updateExportMenuState();
                });
        } else {
            this.data = [];
            this.actionItemsByTagId = {};
            this.updateExportMenuState();
        }
    }

    private delete(id: number, event) {
        const tag = this.data.find(t => t.id === id);
        if (tag.count === 0) {
            this.confirmationService.confirm({
                message: this.translate.instant('tag.list.confirmDeleteMessage', {name: tag.name}),
                accept: () => {
                    this.engineTagService.deleteTag(this.datastore.code, id).subscribe(() => {
                        this.messageService.add({
                            severity: 'success',
                            summary: this.translate.instant('datastore.successSummary'),
                            detail: this.translate.instant('tag.list.deletedSuccess')
                        });
                        this.updateListTable();
                    });
                }
            });
        } else {
            this.messageService.add({ key: 'tags', severity: 'error', summary: this.translate.instant('tag.list.deleteImpossibleSummary'), detail: this.translate.instant('tag.list.deleteImpossibleAffectedDetail')});
        }
    }

    doNouveau() {
        this.editPanel = true;
        this.isNew = true;
        this.initializeForm(null);
    }

    initializeForm(tag: TagDTO) {
        const enable = tag ? tag.enable : true;
        this.form.patchValue({
            id: tag?.id,
            tenant: this.datastore?.code,
            partners: tag?.partners ?? [],
            name: tag?.name,
            description: tag?.description,
            enable
        });
    }
    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    save() {
        const tag: TagDTO = this.getTagDTO();
        const tenant = this.form.value.tenant;
        if (this.isNew) {
            this.engineTagService.createTag(tag, tenant).subscribe(res => {
                this.updateListTable();
                this.editPanel = false;
            });
        } else {
            this.engineTagService.updateTag(tag, tenant, tag.id).subscribe(res => {
                this.updateListTable();
                this.editPanel = false;
            });
        }
    }
    close() {
        this.editPanel = false;
    }

    private getTagDTO() {
        return {
            id: this.form.value.id,
            name: this.form.value.name,
            description: this.form.value.description,
            enable: this.form.value.enable,
            partners: this.form.value.partners
        } as TagDTO;
    }

    // ============ EXPORT/IMPORT METHODS ============

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedTags(),
                disabled: !this.selectedTags || this.selectedTags.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllTags(),
                disabled: !this.data || this.data.length === 0
            }
        ];
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.tagFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.engineTagService.importTagJson(this.datastore.code, jsonContent).subscribe(
                (tag: TagDTO) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('tag.list.importSuccessDetail', {name: tag.name})
                    });
                    this.updateListTable();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.engineTagService.importTagsZip(this.datastore.code, file).subscribe(
            (tags: TagDTO[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('tag.list.importSuccessMultipleDetail', {count: tags.length})
                });
                this.updateListTable();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte un seul tag en JSON
     */
    exportTagJson(tag: TagDTO) {
        this.engineTagService.exportTagJson(this.datastore.code, tag.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `tag-${tag.name}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('tag.list.exportSuccessDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte plusieurs tags en ZIP
     */
    exportSelectedTags() {
        if (!this.selectedTags || this.selectedTags.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('tag.list.noTagSelectedDetail')
            });
            return;
        }

        const ids = this.selectedTags.map(t => t.id);
        this.engineTagService.exportTagsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `tags-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('tag.list.exportSuccessMultipleDetail', {count: this.selectedTags.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte tous les tags en ZIP
     */
    exportAllTags() {
        if (!this.data || this.data.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('tag.list.noTagToExportDetail')
            });
            return;
        }

        const ids = this.data.map(t => t.id);
        this.engineTagService.exportTagsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `tags-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('tag.list.exportSuccessMultipleDetail', {count: this.data.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedTags || this.selectedTags.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.data || this.data.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }

    getItemsListAction(tag: TagDTO): MenuItem[] {
        const cached = this.actionItemsByTagId[tag.id];
        if (cached) {
            return cached;
        }

        const items: MenuItem[] = [
            {
                label: this.translate.instant('subscriptions.edit'),
                icon: 'fas fa-pen',
                permission: 'TAGS.edit',
                command: () => {
                    this.initializeForm(tag);
                    this.isNew = false;
                    this.editPanel = true;
                }
            },
            {
                label: this.translate.instant('subscriptions.delete'),
                icon: 'far fa-trash-alt',
                permission: 'TAGS.delete',
                command: (event) => {
                    this.delete(tag.id, event);
                }
            },
            {
                separator: true
            },
            {
                label: this.translate.instant('subscriptions.exportJson'),
                icon: 'fas fa-download',
                permission: 'TAGS.edit',
                command: () => {
                    this.exportTagJson(tag);
                }
            }
        ];

        this.actionItemsByTagId[tag.id] = items;
        return items;
    }

}
